// Importing necessary modules and hooks
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useThree } from "@react-three/fiber";
import { useSnapshot } from "valtio";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { state } from "./store.js";
import { OrbitControls, PerspectiveCamera } from "@react-three/drei";

export default function AnimateCamera({ enabled }) {
  const snap = useSnapshot(state);
  const controlsRef = useRef(null);
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);

  // Track pointer position and movement for drag detection
  const pointerStartRef = useRef({ x: 0, y: 0 });
  const pointerCurrentRef = useRef({ x: 0, y: 0 });
  const isPointerDownRef = useRef(false);
  const hasMovedRef = useRef(false);
  const dragThreshold = 5; // Minimum pixels to consider as drag

  // Detect actual dragging based on pointer movement distance
  useEffect(() => {
    const canvas = gl.domElement;

    const handlePointerDown = (e) => {
      isPointerDownRef.current = true;
      hasMovedRef.current = false;
      pointerStartRef.current = { x: e.clientX, y: e.clientY };
      pointerCurrentRef.current = { x: e.clientX, y: e.clientY };
    };

    const handlePointerMove = (e) => {
      if (!isPointerDownRef.current) return;

      pointerCurrentRef.current = { x: e.clientX, y: e.clientY };

      // Calculate distance moved
      const deltaX = pointerCurrentRef.current.x - pointerStartRef.current.x;
      const deltaY = pointerCurrentRef.current.y - pointerStartRef.current.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // If moved beyond threshold, consider it a drag
      if (distance > dragThreshold && !hasMovedRef.current) {
        hasMovedRef.current = true;
        state.isDraggingCamera = true;
        state.allowHover = false;
        state.allowClick = false;

        // Force reset hover state when camera manipulation starts
        state.hoveredModel = false;
        state.hoveredElement = null;
        state.hoveredElementName = "";
      }
    };

    const handlePointerUp = () => {
      isPointerDownRef.current = false;

      // Only if we actually dragged, re-enable interactions after release
      if (hasMovedRef.current) {
        state.isDraggingCamera = false;

        // Re-enable interactions only if intro is finished
        if (!snap.intro && snap.finishedIntro) {
          state.allowHover = true;
          state.allowClick = true;
        }
      }

      hasMovedRef.current = false;
    };

    // Add event listeners to canvas
    canvas.addEventListener("pointerdown", handlePointerDown);
    canvas.addEventListener("pointermove", handlePointerMove);
    canvas.addEventListener("pointerup", handlePointerUp);
    canvas.addEventListener("pointercancel", handlePointerUp);

    return () => {
      canvas.removeEventListener("pointerdown", handlePointerDown);
      canvas.removeEventListener("pointermove", handlePointerMove);
      canvas.removeEventListener("pointerup", handlePointerUp);
      canvas.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [gl, snap.intro, snap.finishedIntro]);

  function setControls(
    value,
    maxDistance,
    minDistance,
    maxPolarAngle,
    minPolarAngle
  ) {
    controlsRef.current.enabled = value;
    controlsRef.current.maxDistance = value ? maxDistance : Infinity;
    controlsRef.current.minDistance = value ? minDistance : 0;
    controlsRef.current.maxPolarAngle = value ? maxPolarAngle : Math.PI * 2;
    controlsRef.current.minPolarAngle = value ? minPolarAngle : 0;
  }

  useEffect(() => {
    if (snap.isMobile) {
      // Persist mobile optimization preference only; do not change runtime state
      try {
        const saved =
          typeof localStorage !== "undefined" &&
          localStorage.getItem("qualitySettings");
        if (!saved) {
          localStorage.setItem("qualitySettings", "performance");
        }
      } catch (err) {
        // Ignore storage errors (quota/privacy mode)
      }
    }
  }, [snap.isMobile]);

  // Control hover and click interactions during intro
  useEffect(() => {
    if (snap.intro || !snap.finishedIntro) {
      state.allowHover = false;
      state.allowClick = false;
    } else if (!state.isDraggingCamera) {
      // Only enable interactions if intro is finished and not currently dragging
      state.allowHover = true;
      state.allowClick = true;
    }
  }, [snap.intro, snap.finishedIntro]);

  useEffect(() => {
    if (snap.intro) return;

    if (snap.viewType === "deck") {
      camera.position.set(-40, 2, 0);
      controlsRef.current.target.set(-38, 2, 0);
      setControls(true, 10, 2, Math.PI / 2 - 0.001, 0.4);
      camera.fov = 85;
      camera.updateProjectionMatrix();
    } else if (snap.viewType === "bow") {
      // Add bow view camera position here when needed
      camera.position.set(55, 15, 0);
      controlsRef.current.target.set(30, 10, 0);
      setControls(true, 10, 2, Math.PI / 2 - 0.001, 0.4);
      camera.fov = 60;
      camera.updateProjectionMatrix();
    } else if (snap.viewType === "cabin") {
      camera.position.set(-5, 9, 0);
      controlsRef.current.target.set(5, 5, 0);
      setControls(true, 10, 1, Math.PI / 2 - 0.4, 1.1);
      camera.fov = 85;
      camera.updateProjectionMatrix();
    } else {
      // Main view
      camera.position.set(-1, 160, -1);
      controlsRef.current.target.set(0, 1, 0);
      setControls(true, 150, 60, Math.PI / 2 - 0.001, 0.4);
      camera.fov = 40;
      camera.updateProjectionMatrix();
    }
  }, [snap.viewType]);

  useGSAP(
    () => {
      const tl = gsap.timeline();
      // setControls(true);

      if (!snap.intro && !snap.panel && snap.viewType === "main") {
        tl.to(camera.position, {
          x: -120,
          y: 5,
          z: -80,
          ease: "power1.inOut",
          duration: enabled ? 8 : 0,
          onStart: () => setControls(true, Infinity, 0, Math.PI * 2, 0),
          // onUpdate: () => {
          //   camera.lookAt(0, 1, 0);
          //   // controlsRef.current.update();
          // },
        })
          .to(
            controlsRef.current.target,
            {
              x: 2,
              y: 1,
              z: 0,
              duration: enabled ? 8 : 0,
              ease: "power1.inOut",
            },
            "<"
          )
          .to(
            camera,
            {
              fov: 40,
              duration: enabled ? 8 : 0,
              ease: "power1.inOut",

              onUpdate: (self) => {
                // console.log(tl.progress())
                camera.updateProjectionMatrix();

                if (tl.progress() > 0.6) {
                  state.finishedIntro = true;
                }
                // camera.lookAt(0, 1, 0);
                // controlsRef.current.update();
              },
              onComplete: () => {
                setControls(true, 150, 60, Math.PI / 2 - 0.001, 0.4);
                if (!enabled) {
                  state.finishedIntro = true;
                  state.finishedMainLoading = true;
                }
              },
            },
            "<"
          );
        // .to(controlsRef.current.target, {
        //   x: 2,
        //   y: 1,
        //   z: 0,
        //   duration: enabled ? 3 : 0,
        //   ease: "power1.inOut",

        // });

        // .to(
        //   camera.position,
        //   {
        //     x: snap.isMobile ? 0 : -90,
        //     y: snap.isMobile ? 15 : 5,
        //     z: snap.isMobile ? 45 : -100,
        //     duration: enabled ? 3 : 0,
        //     ease: "power1.inOut",

        //   },
        //   "<"
        // );
      }
    },
    { dependencies: [snap.intro] }
  );

  return (
    <>
      {/* <PerspectiveCamera position={[0, 160, 0]} fov={85} makeDefault /> */}
      <OrbitControls
        zoomSpeed={0.5}
        autoRotate={snap.autoRotate}
        autoRotateSpeed={0.5}
        enabled={false}
        ref={controlsRef}
        makeDefault
        enablePan={false}
        target={[0.01, 1, 0.01]}
        dampingFactor={0.16}
        // maxPolarAngle={Math.PI / 2 + 0.02}
        // minPolarAngle={1.4}
        // maxDistance={150}
        // minDistance={60}
      />
    </>
  );
}
